import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    State,
    PricingPlan,
    Transaction,
    PaymentMethod,
    EvaluatedCriteria,
    ExaminerReportData,
    CodingExamExaminerData,
    PostedJob,
    MemberDetail,
    ExamStatsTab,
    ExtraServiceDetail,
} from './types';
import {
    getJobsThunk,
    postNewJobThunk,
    getExaminersThunk,
    getExamFilesThunk,
    getLogoImageThunk,
    getProfileDataThunk,
    getStripeCustomerThunk,
    getUserAttributesThunk,
    getPaymentMethodsThunk,
    updateProfileInfoThunk,
    requestChangePlanThunk,
    getExaminerProfileThunk,
    sendSupportMessageThunk,
    deletePaymentMethodThunk,
    getSubsciptionHistoryThunk,
    getTransactionsHistoryThunk,
    getRecruitingInterviewQuestionsThunk,
} from './thunks';

const initialState: State = {
    initialLoading: true,
    selectedApplicantsIds: [],
    examinerReportData: null,
    companyLogo: null,
    companyLogoModal: false,
    incompleteProfileModal: false,
    stripeCustomer: null,
    profile: null,
    fetchingProfile: false,
    sendingSupportMessage: false,
    changePlanModal: false,
    selectedPricingPlan: null,
    purchasePlanModal: false,
    plansModalShow: false,
    chooseCreditCardModal: false,
    addNewCardModal: false,
    postJobStep: 'JobDetail',
    newJob: null,
    postingNewJob: false,
    deleteCardModal: false,
    logoImage: null,
    sideBarShow: false,
    sideBarExpanded: false,
    userAttributes: null,
    getHelpModalShow: false,
    postedJobs: [],
    examStatics: null,
    editCardModal: false,
    fetchingPostedJobs: true,
    fetchingExaminersDetail: false,
    selectedExaminerId: null,
    confirmEndExamModal: false,
    examinerProfile: null,
    examinerProfileModal: false,
    examinerCodingExamData: null,
    fetchingExaminerProfile: false,
    paymentMethods: [],
    selectedPlanSeatCount: 1,
    fetchingpPaymentMethods: false,
    transactionsHistory: [],
    selectedPaymentMethod: null,
    deletingPaymentMethod: false,
    selectedTransaction: null,
    transactionDetailModal: false,
    editAccountInfoModal: false,
    editCompanyInfoModal: false,
    updatingProfileInfo: false,
    croppedCompanyLogo: null,
    videoPlayerModal: false,
    videoSrc: null,
    videoSubtitleSrc: null,
    fetchingVideoSrc: false,
    requestingChangePlan: false,
    examinerReportModal: false,
    recruitingInterviewReportModal: false,
    recruitingInterviewQuestions: [],
    addNewInterviewQuestionModal: false,
    resetInterviewFormModal: false,
    selectedInterviewApplicant: null,
    contactSalesModal: false,
    paymentRetryMode: false,
    subscriptionHistory: null,
    confirmCancelSubscriptionModal: false,
    fetchingSubscriptionHistory: false,
    extractFileTextErrorModal: false,
    jobDetailModal: false,
    selectedJob: null,
    changePasswordModal: false,
    inviteMemberModal: false,
    confirmLeaveOrganizationModal: false,
    confirmJoinOrganizationModal: false,
    inviteMemberToken: null,
    confirmRemoveMemberModal: false,
    selectedMemberDetail: null,
    contactAdminModal: false,
    examStatsTab: undefined,
    examStatsJobInfo: null,
    recruitingInterviewStats: null,
    multipleChoiceExamStats: null,
    codingExamStats: null,
    personalInterviewStats: null,
    finalResultStats: null,
    examShareGuideModal: false,
    submittingJobModal: false,
    editPhoneNumberModal: false,
    editEmailModal: false,
    addExtraApplicantModal: false,
    addExtraJobPostModal: false,
    addExtraMemberModal: false,
    extraServicesPaymentModal: false,
    extraServiceDetail: null,
    regenerateMultipleChoiceQuestionByAIModal: false,
    editMultipleChoiceQuestionManuallyModal: false,
    editJobExamDeadlineModal: false,
    preparingExamsModal: false,
    copyJobLinkWarningModal: false,
    videoUploadIssueModal: false,
    jobsSearchShow: false,
};

export const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        setSideBarShow: (state, { payload }) => {
            state.sideBarShow = payload;
        },
        setSideBarExpanded: (state, { payload }) => {
            state.sideBarExpanded = payload;
        },
        setGetHelpModalShow: (state, action) => {
            state.getHelpModalShow = action.payload;
        },
        setNewJobDetail: (state, action) => {
            state.newJob = action.payload;
        },
        setPostJobStep: (state, action) => {
            state.postJobStep = action.payload;
        },
        setSelectedExaminerId: (state, action) => {
            state.selectedExaminerId = action.payload;
        },
        setExamStatics: (state, action) => {
            state.examStatics = action.payload;
        },
        setConfirmEndExam: (state, action: PayloadAction<boolean>) => {
            state.confirmEndExamModal = action.payload;
        },
        setExaminerProfile: (state, action: PayloadAction<any>) => {
            state.examinerProfile = action.payload;
        },
        setExaminerProfileModal: (state, action: PayloadAction<boolean>) => {
            state.examinerProfileModal = action.payload;
        },
        setExaminerCodingExamData: (state, action: PayloadAction<CodingExamExaminerData | null>) => {
            state.examinerCodingExamData = action.payload;
        },
        setPlansModalShow: (state, action: PayloadAction<boolean>) => {
            state.plansModalShow = action.payload;
        },
        setPricingPlan: (state, action: PayloadAction<PricingPlan | null>) => {
            state.selectedPricingPlan = action.payload;
        },
        setAddNewCardModal: (state, action: PayloadAction<boolean>) => {
            state.addNewCardModal = action.payload;
        },
        setDeleteCardModal: (state, action: PayloadAction<boolean>) => {
            state.deleteCardModal = action.payload;
        },
        setEditCardModal: (state, action: PayloadAction<boolean>) => {
            state.editCardModal = action.payload;
        },
        setPurchasePlanModal: (state, action: PayloadAction<boolean>) => {
            state.purchasePlanModal = action.payload;
        },
        setChooseCreditCardModal: (state, action: PayloadAction<boolean>) => {
            state.chooseCreditCardModal = action.payload;
        },
        setSelectedPlanSeatCount: (state, action: PayloadAction<number>) => {
            state.selectedPlanSeatCount = action.payload;
        },
        setSelectedPaymentMethod: (state, action: PayloadAction<PaymentMethod | null>) => {
            state.selectedPaymentMethod = action.payload;
        },
        setSelectedTransaction: (state, action: PayloadAction<Transaction | null>) => {
            state.selectedTransaction = action.payload;
        },
        setTransactionDetailModal: (state, action: PayloadAction<boolean>) => {
            state.transactionDetailModal = action.payload;
        },
        setEditAccountInfoModal: (state, action: PayloadAction<boolean>) => {
            state.editAccountInfoModal = action.payload;
        },
        setEditCompanyInfoModal: (state, action: PayloadAction<boolean>) => {
            state.editCompanyInfoModal = action.payload;
        },
        setChangePlanModal: (state, action: PayloadAction<boolean>) => {
            state.changePlanModal = action.payload;
        },
        setIncompleteProfileModal: (state, action: PayloadAction<boolean>) => {
            if (action.payload && state.confirmJoinOrganizationModal) return;
            state.incompleteProfileModal = action.payload;
        },
        setCompanyLogoModal: (state, action: PayloadAction<boolean>) => {
            state.companyLogoModal = action.payload;
        },
        setCompanyLogo: (state, action: PayloadAction<string | null>) => {
            state.companyLogo = action.payload;
        },
        setCroppedCompanyLogo: (state, action: PayloadAction<string | null>) => {
            state.croppedCompanyLogo = action.payload;
        },
        setVideoPlayerModal: (state, action: PayloadAction<boolean>) => {
            state.videoPlayerModal = action.payload;
        },
        setVideoSrc: (state, action: PayloadAction<string | null>) => {
            state.videoSrc = action.payload;
        },
        setVideoSubtitleSrc: (state, action: PayloadAction<string | null>) => {
            state.videoSubtitleSrc = action.payload;
        },
        setExaminerReportData: (state, action: PayloadAction<ExaminerReportData | null>) => {
            state.examinerReportData = action.payload;
        },
        setExaminerReportModal: (state, action: PayloadAction<boolean>) => {
            state.examinerReportModal = action.payload;
        },
        setSelectedApplicantsIds: (state, action: PayloadAction<string[]>) => {
            state.selectedApplicantsIds = action.payload;
        },
        setRecruitingInterviewReportModal: (state, action: PayloadAction<boolean>) => {
            state.recruitingInterviewReportModal = action.payload;
        },
        setAddNewInterviewQuestionModal: (state, action: PayloadAction<boolean>) => {
            state.addNewInterviewQuestionModal = action.payload;
        },
        setResetInterviewFormModal: (state, action: PayloadAction<boolean>) => {
            state.resetInterviewFormModal = action.payload;
        },
        setSelectedInterviewApplicant: (state, action: PayloadAction<EvaluatedCriteria | null>) => {
            state.selectedInterviewApplicant = action.payload;
        },
        setContactSalesModal: (state, action: PayloadAction<boolean>) => {
            state.contactSalesModal = action.payload;
        },
        setPaymentRetryMode: (state, action: PayloadAction<boolean>) => {
            state.paymentRetryMode = action.payload;
        },
        setConfirmCancelSubscriptionModal: (state, action: PayloadAction<boolean>) => {
            state.confirmCancelSubscriptionModal = action.payload;
        },
        setExtractFileTextErrorModal: (state, action: PayloadAction<boolean>) => {
            state.extractFileTextErrorModal = action.payload;
        },
        setJobDetailModal: (state, action: PayloadAction<boolean>) => {
            state.jobDetailModal = action.payload;
        },
        setSelectedJob: (state, action: PayloadAction<PostedJob | null>) => {
            state.selectedJob = action.payload;
        },
        setChangePasswordModal: (state, action: PayloadAction<boolean>) => {
            state.changePasswordModal = action.payload;
        },
        setInviteMemberModal: (state, action: PayloadAction<boolean>) => {
            state.inviteMemberModal = action.payload;
        },
        setConfirmLeaveOrganizationModal: (state, action: PayloadAction<boolean>) => {
            state.confirmLeaveOrganizationModal = action.payload;
        },
        setConfirmJoinOrganizationModal: (state, action: PayloadAction<boolean>) => {
            state.confirmJoinOrganizationModal = action.payload;
        },
        setInviteMemberToken: (state, action: PayloadAction<string>) => {
            state.inviteMemberToken = action.payload;
        },
        setConfirmRemoveMemberModal: (state, action: PayloadAction<boolean>) => {
            state.confirmRemoveMemberModal = action.payload;
        },
        setSelectedMemberDetail: (state, action: PayloadAction<MemberDetail | null>) => {
            state.selectedMemberDetail = action.payload;
        },
        setContactAdminModal: (state, action: PayloadAction<boolean>) => {
            state.contactAdminModal = action.payload;
        },
        setExamStatsTab: (state, action: PayloadAction<ExamStatsTab>) => {
            state.examStatsTab = action.payload;
        },
        resetExamStatistics: (state) => {
            state.codingExamStats = null;
            state.examStatsJobInfo = null;
            state.finalResultStats = null;
            state.examStatsTab = undefined;
            state.selectedApplicantsIds = [];
            state.personalInterviewStats = null;
            state.multipleChoiceExamStats = null;
            state.recruitingInterviewStats = null;
        },
        setExamShareGuideModal: (state, action: PayloadAction<boolean>) => {
            state.examShareGuideModal = action.payload;
        },
        setSubmittingJobModal: (state, action: PayloadAction<boolean>) => {
            state.submittingJobModal = action.payload;
        },
        setEditPhoneNumberModal: (state, action: PayloadAction<boolean>) => {
            state.editPhoneNumberModal = action.payload;
        },
        setEditEmailModal: (state, action: PayloadAction<boolean>) => {
            state.editEmailModal = action.payload;
        },
        setAddExtraApplicantModal: (state, action: PayloadAction<boolean>) => {
            state.addExtraApplicantModal = action.payload;
        },
        setAddExtraJobPostModal: (state, action: PayloadAction<boolean>) => {
            state.addExtraJobPostModal = action.payload;
        },
        setAddExtraMemberModal: (state, action: PayloadAction<boolean>) => {
            state.addExtraMemberModal = action.payload;
        },
        setExtraServicesPaymentModal: (state, action: PayloadAction<boolean>) => {
            state.extraServicesPaymentModal = action.payload;
        },
        setExtraServiceDetail: (state, action: PayloadAction<ExtraServiceDetail | null>) => {
            state.extraServiceDetail = action.payload;
        },
        setRegenerateMultipleChoiceQuestionByAIModal: (state, action: PayloadAction<boolean>) => {
            state.regenerateMultipleChoiceQuestionByAIModal = action.payload;
        },
        setEditMultipleChoiceQuestionManuallyModal: (state, action: PayloadAction<boolean>) => {
            state.editMultipleChoiceQuestionManuallyModal = action.payload;
        },
        setEditJobExamDeadlineModal: (state, action: PayloadAction<boolean>) => {
            state.editJobExamDeadlineModal = action.payload;
        },
        setPreparingExamsModal: (state, action: PayloadAction<boolean>) => {
            state.preparingExamsModal = action.payload;
        },
        setCopyJobLinkWarningModal: (state, action: PayloadAction<boolean>) => {
            state.copyJobLinkWarningModal = action.payload;
        },
        setVideoUploadIssueModal: (state, action: PayloadAction<boolean>) => {
            state.videoUploadIssueModal = action.payload;
        },
        setJobsSearchShow: (state, action: PayloadAction<boolean>) => {
            state.jobsSearchShow = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getProfileDataThunk.pending, (state, action) => {
                state.fetchingProfile = true;
            })
            .addCase(getProfileDataThunk.fulfilled, (state, action) => {
                state.profile = action.payload;
                state.fetchingProfile = false;
            })
            .addCase(getProfileDataThunk.rejected, (state, action) => {
                state.fetchingProfile = false;
            })
            .addCase(getLogoImageThunk.fulfilled, (state, action) => {
                state.logoImage = action.payload;
            })
            .addCase(getUserAttributesThunk.fulfilled, (state, action) => {
                state.userAttributes = action.payload;
            })
            .addCase(postNewJobThunk.pending, (state) => {
                state.submittingJobModal = true;
                state.postingNewJob = true;
            })
            .addCase(postNewJobThunk.fulfilled, (state, action) => {
                state.postJobStep = 'Approval';
                state.postingNewJob = false;
            })
            .addCase(postNewJobThunk.rejected, (state) => {
                state.postingNewJob = false;
            })
            .addCase(getJobsThunk.pending, (state) => {
                state.fetchingPostedJobs = true;
            })
            .addCase(getJobsThunk.fulfilled, (state, action) => {
                state.initialLoading = false;
                state.fetchingPostedJobs = false;
                state.postedJobs = action.payload;
            })
            .addCase(getJobsThunk.rejected, (state) => {
                state.fetchingPostedJobs = false;
            })
            .addCase(getExaminersThunk.pending, (state) => {
                state.fetchingExaminersDetail = true;
            })
            .addCase(getExaminersThunk.fulfilled, (state, action) => {
                state.fetchingExaminersDetail = false;
                state.examStatsJobInfo = action.payload?.jobInfo || null;
                // state.examStatics = action.payload;
                switch (action.payload.type) {
                    case 'recruitingInterview':
                        state.recruitingInterviewStats = action.payload;
                        state.examStatsTab = 'recruitingInterview';
                        break;
                    case 'multipleChoice':
                        state.multipleChoiceExamStats = action.payload;
                        state.examStatsTab = 'multipleChoice';
                        break;
                    case 'coding':
                        state.codingExamStats = action.payload;
                        state.examStatsTab = 'coding';
                        break;
                    case 'interview':
                        state.personalInterviewStats = action.payload;
                        state.examStatsTab = 'interview';
                        break;
                    case 'finalResult':
                        state.finalResultStats = action.payload;
                        state.examStatsTab = 'finalResult';
                        break;
                    default:
                        return;
                }
            })
            .addCase(getExaminersThunk.rejected, (state) => {
                state.fetchingExaminersDetail = false;
            })
            .addCase(getExaminerProfileThunk.pending, (state) => {
                state.fetchingExaminerProfile = true;
            })
            .addCase(getExaminerProfileThunk.fulfilled, (state, action) => {
                state.fetchingExaminerProfile = false;
                state.examinerProfile = action.payload;
            })
            .addCase(getExaminerProfileThunk.rejected, (state) => {
                state.fetchingExaminerProfile = false;
            })
            .addCase(getPaymentMethodsThunk.fulfilled, (state, action) => {
                state.paymentMethods = action.payload;
                state.fetchingpPaymentMethods = false;
            })
            .addCase(getPaymentMethodsThunk.pending, (state) => {
                state.fetchingpPaymentMethods = true;
            })
            .addCase(getPaymentMethodsThunk.rejected, (state) => {
                state.fetchingpPaymentMethods = false;
            })
            .addCase(getStripeCustomerThunk.fulfilled, (state, action) => {
                state.stripeCustomer = action.payload;
            })
            .addCase(getTransactionsHistoryThunk.fulfilled, (state, action) => {
                state.transactionsHistory = action.payload;
            })
            .addCase(deletePaymentMethodThunk.fulfilled, (state) => {
                state.deleteCardModal = false;
                state.deletingPaymentMethod = false;
            })
            .addCase(deletePaymentMethodThunk.pending, (state) => {
                state.deletingPaymentMethod = true;
            })
            .addCase(deletePaymentMethodThunk.rejected, (state) => {
                state.deletingPaymentMethod = false;
            })
            .addCase(updateProfileInfoThunk.pending, (state) => {
                state.updatingProfileInfo = true;
            })
            .addCase(updateProfileInfoThunk.fulfilled, (state) => {
                state.updatingProfileInfo = false;
                state.editAccountInfoModal = false;
                state.editCompanyInfoModal = false;
            })
            .addCase(updateProfileInfoThunk.rejected, (state) => {
                state.updatingProfileInfo = false;
            })
            .addCase(sendSupportMessageThunk.pending, (state) => {
                state.sendingSupportMessage = true;
            })
            .addCase(sendSupportMessageThunk.fulfilled, (state) => {
                state.sendingSupportMessage = false;
            })
            .addCase(sendSupportMessageThunk.rejected, (state) => {
                state.sendingSupportMessage = false;
            })
            .addCase(getExamFilesThunk.rejected, (state) => {
                state.fetchingVideoSrc = false;
                state.videoUploadIssueModal = true;
            })
            .addCase(getExamFilesThunk.pending, (state) => {
                state.fetchingVideoSrc = true;
            })
            .addCase(getExamFilesThunk.fulfilled, (state, action) => {
                state.videoSrc = action.payload;
                state.videoPlayerModal = true;
                state.fetchingVideoSrc = false;
            })
            .addCase(requestChangePlanThunk.pending, (state) => {
                state.requestingChangePlan = true;
            })
            .addCase(requestChangePlanThunk.fulfilled, (state) => {
                state.requestingChangePlan = false;
                state.changePlanModal = false;
                state.purchasePlanModal = false;
            })
            .addCase(requestChangePlanThunk.rejected, (state) => {
                state.requestingChangePlan = false;
            })
            .addCase(getRecruitingInterviewQuestionsThunk.fulfilled, (state, action) => {
                state.recruitingInterviewQuestions = action.payload;
            })
            .addCase(getSubsciptionHistoryThunk.fulfilled, (state, action) => {
                state.subscriptionHistory = action.payload;
                state.fetchingSubscriptionHistory = false;
            })
            .addCase(getSubsciptionHistoryThunk.pending, (state, action) => {
                state.fetchingSubscriptionHistory = true;
            })
            .addCase(getSubsciptionHistoryThunk.rejected, (state, action) => {
                state.fetchingSubscriptionHistory = false;
            })
    }
})

export const {
    setSubmittingJobModal,
    setExamShareGuideModal,
    resetExamStatistics,
    setExamStatsTab,
    setConfirmRemoveMemberModal,
    setSelectedMemberDetail,
    setSelectedInterviewApplicant,
    setInviteMemberToken,
    setConfirmJoinOrganizationModal,
    setExaminerReportModal,
    setExaminerReportData,
    setRecruitingInterviewReportModal,
    setJobsSearchShow,
    setNewJobDetail,
    setExamStatics,
    setPostJobStep,
    setSideBarShow,
    setSideBarExpanded,
    setCompanyLogo,
    setCompanyLogoModal,
    setChangePlanModal,
    setPricingPlan,
    setSelectedTransaction,
    setSelectedPlanSeatCount,
    setTransactionDetailModal,
    setChooseCreditCardModal,
    setPurchasePlanModal,
    setEditCardModal,
    setDeleteCardModal,
    setConfirmEndExam,
    setAddNewCardModal,
    setPlansModalShow,
    setExaminerProfile,
    setGetHelpModalShow,
    setSelectedExaminerId,
    setExaminerProfileModal,
    setEditAccountInfoModal,
    setIncompleteProfileModal,
    setEditCompanyInfoModal,
    setCroppedCompanyLogo,
    setVideoPlayerModal,
    setVideoSrc,
    setVideoSubtitleSrc,
    setSelectedPaymentMethod,
    setExaminerCodingExamData,
    setSelectedApplicantsIds,
    setResetInterviewFormModal,
    setAddNewInterviewQuestionModal,
    setContactSalesModal,
    setPaymentRetryMode,
    setConfirmCancelSubscriptionModal,
    setExtractFileTextErrorModal,
    setJobDetailModal,
    setSelectedJob,
    setChangePasswordModal,
    setInviteMemberModal,
    setContactAdminModal,
    setConfirmLeaveOrganizationModal,
    setEditPhoneNumberModal,
    setEditEmailModal,
    setAddExtraApplicantModal,
    setAddExtraJobPostModal,
    setAddExtraMemberModal,
    setExtraServicesPaymentModal,
    setExtraServiceDetail,
    setRegenerateMultipleChoiceQuestionByAIModal,
    setEditMultipleChoiceQuestionManuallyModal,
    setEditJobExamDeadlineModal,
    setPreparingExamsModal,
    setCopyJobLinkWarningModal,
    setVideoUploadIssueModal,
} = workspaceSlice.actions;
